import { useState, type ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { LayoutGrid, List, PawPrint, Square } from "lucide-react";

import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";
import "../pages/PensionOverzichten.css";
import { IndeelbareTabel } from "./IndeelbareTabel";

/**
 * De drie weergaven van Pensionoverzichten — grote tegels, kleine tegels en een lijst — voor de dagelijkse lijsten
 * (Wegen, Bodycheck, Borstelen, Medicatie, Voerlijst). Maarten, 2026-10-05: "wil kunnen kiezen tussen de 3
 * lay-outs, grote tegels, kleine tegels en lijst".
 *
 * <p>Tabel en tegels komen uit dezelfde kolommen, zodat een lijst zijn cellen maar één keer beschrijft. In een tegel
 * staat het dier bovenaan met foto, klant en verblijf eronder, de overige kolommen als gegevens, en de actie
 * (wegen, afvinken …) onderaan.
 */

export type Weergave = "GROOT" | "KLEIN" | "LIJST";

export interface Kolom<R> {
  kop: string;
  cel: (r: R) => ReactNode;
  /** dier → naam van de tegel; klant en verblijf → de regel eronder; actie → onderaan; anders een gegeven. */
  rol?: "dier" | "klant" | "verblijf" | "actie";
  klasse?: string;
}

export interface Rij {
  key: string | number;
  "data-signaal"?: string;
  "data-tewegen"?: string;
}

const OPSLAG = "fawkes.werklijst.weergave.";

/** Standaard kleine tegels, net als Pensionoverzichten; de keuze wordt per lijst onthouden. */
export function useWeergave(lijst: string): [Weergave, (w: Weergave) => void] {
  const [w, setW] = useState<Weergave>(() => {
    try {
      const v = globalThis.localStorage?.getItem(OPSLAG + lijst);
      return v === "GROOT" || v === "KLEIN" || v === "LIJST" ? v : "KLEIN";
    } catch {
      return "KLEIN";
    }
  });
  const zet = (nieuw: Weergave) => {
    setW(nieuw);
    try {
      globalThis.localStorage?.setItem(OPSLAG + lijst, nieuw);
    } catch {
      /* zonder opslag onthouden we het alleen deze sessie */
    }
  };
  return [w, zet];
}

export function WeergaveKeuze({ waarde, onKies }: { waarde: Weergave; onKies: (w: Weergave) => void }) {
  const t = useTranslate();
  return (
    <span className="po-segment po-segment--iconen" role="group" aria-label={t("pensionOverzichten.weergave")}>
      {(["GROOT", "KLEIN", "LIJST"] as Weergave[]).map((w) => {
        const Icoon = w === "GROOT" ? Square : w === "KLEIN" ? LayoutGrid : List;
        return (
          <button key={w} type="button" aria-pressed={waarde === w} aria-label={t(`pensionOverzichten.weergave.${w}`)}
            title={t(`pensionOverzichten.weergave.${w}`)} onClick={() => onKies(w)}>
            <Icoon size={16} aria-hidden="true" />
          </button>
        );
      })}
    </span>
  );
}

export function Werklijst<R>({ regels, kolommen, weergave, rij, dierId, lijst }: {
  regels: R[];
  kolommen: Kolom<R>[];
  weergave: Weergave;
  rij: (r: R) => Rij;
  dierId: (r: R) => number;
  /** Welke lijst dit is (dezelfde naam als bij {@link useWeergave}): de sleutel van de kolomindeling. */
  lijst: string;
}) {
  const fotos = useAnimalPhotoUrls(weergave === "LIJST" ? [] : regels.map(dierId));
  if (weergave === "LIJST") {
    return (
      <div className="pension-werklijst__tabelwrap lijst">
        <IndeelbareTabel
          tabelKey={`werklijst-${lijst}`}
          columns={kolommen.map((k) => ({
            key: k.kop,
            header: k.kop,
            // De celklasse zat op de td; die kent de tabel niet, dus hangt hij nu om de inhoud.
            cell: (r: R) => (k.klasse ? <span className={k.klasse}>{k.cel(r)}</span> : k.cel(r)),
            vast: k.rol === "actie",
          }))}
          rows={regels}
          getRowKey={(r) => String(rij(r).key)}
          // De rij droeg data-signaal en data-tewegen voor zijn kleur; een tabel geeft een rij alleen
          // een klasse mee, dus staat hetzelfde onderscheid nu in de klassenaam.
          rowClassName={(r) => {
            const { "data-signaal": signaal, "data-tewegen": teDoen } = rij(r);
            return [
              signaal === "ACTIE_NODIG" ? "pension-werklijst__rij--actie" : undefined,
              teDoen !== undefined ? "pension-werklijst__rij--tedoen" : undefined,
            ].filter(Boolean).join(" ") || undefined;
          }}
          empty=""
        />
      </div>
    );
  }

  const dier = kolommen.find((k) => k.rol === "dier");
  const onder = kolommen.filter((k) => k.rol === "klant" || k.rol === "verblijf");
  const gegevens = kolommen.filter((k) => !k.rol);
  const actie = kolommen.find((k) => k.rol === "actie");
  return (
    <div className="po werklijst-tegels">
      <div className="po-raster" data-soort={weergave}>
        {regels.map((r) => {
          const { key, ...attr } = rij(r);
          const url = fotos[String(dierId(r))];
          const foto = (klasse: string) =>
            url ? <img className={klasse} src={url} alt="" />
              : <span className={`${klasse} po-foto--leeg`} aria-hidden="true"><PawPrint size={28} /></span>;
          return (
            <article key={key} {...attr} className={`po-tegel po-tegel--${weergave === "GROOT" ? "groot" : "klein"} werklijst-tegel`}>
              {weergave === "GROOT"
                ? <div className="po-tegel__banner">{foto("po-tegel__bannerfoto")}</div>
                : <div className="po-tegel__fotovak">{foto("po-tegel__foto")}</div>}
              <div className="po-tegel__inhoud">
                {dier && <div className="werklijst-tegel__naam">{dier.cel(r)}</div>}
                {onder.length > 0 && (
                  <span className="po-onder">
                    {onder.map((k, i) => (
                      <span key={k.kop}>{i > 0 && " • "}{k.cel(r)}</span>
                    ))}
                  </span>
                )}
                {gegevens.length > 0 && (
                  <dl className="po-gegevens">
                    {gegevens.map((k) => (
                      <div key={k.kop} className="po-gegevens__rij">
                        <dt>{k.kop}</dt>
                        <dd>{k.cel(r)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                {actie && <div className="werklijst-tegel__actie">{actie.cel(r)}</div>}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
